/**
 * OrchestratorNode - Workflow step nodes with editable prompts
 * Types: query_understanding, direct_response, synthesis
 * These nodes have systemMessage that can be customized
 */

import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import type { OrchestratorNodeData, OrchestratorNodeType } from '../../../../stores/workflow/types';

interface OrchestratorNodeProps {
  data: OrchestratorNodeData;
  selected?: boolean;
}

const orchestratorTypeConfig: Record<OrchestratorNodeType, { color: string; bgGradient: string; icon: React.ReactNode; label: string }> = {
  query_understanding: {
    color: 'violet',
    bgGradient: 'from-violet-500 to-violet-700',
    label: 'Query Understanding',
    icon: (
      <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
      </svg>
    )
  },
  direct_response: {
    color: 'emerald',
    bgGradient: 'from-emerald-500 to-emerald-700',
    label: 'Direct Response',
    icon: (
      <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' />
      </svg>
    )
  },
  synthesis: {
    color: 'rose',
    bgGradient: 'from-rose-500 to-rose-700',
    label: 'Synthesis',
    icon: (
      <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z' />
      </svg>
    )
  }
};

export const OrchestratorNode = memo(({ data, selected }: OrchestratorNodeProps) => {
  const config = orchestratorTypeConfig[data.nodeType] || orchestratorTypeConfig.query_understanding;
  const hasPrompt = data.systemMessage && data.systemMessage.trim().length > 0;

  return (
    <div
      className={`
        min-w-[200px] rounded-xl border-2 bg-white shadow-lg transition-all
        dark:bg-dark-background-secondary
        ${selected ? 'border-primary ring-4 ring-primary/20' : 'border-gray-200 dark:border-border-dark'}
        ${!hasPrompt ? 'border-dashed border-yellow-400' : ''}
      `}
    >
      {/* Input Handle (top) */}
      <Handle type='target' position={Position.Top} className='!h-3 !w-3 !border-2 !border-white !bg-gray-400' />

      {/* Header with gradient */}
      <div className={`flex items-center gap-2 rounded-t-lg bg-gradient-to-r ${config.bgGradient} px-4 py-2`}>
        <div className='text-white'>{config.icon}</div>
        <div className='flex-1 font-semibold text-white'>{data.label}</div>
        {/* Editable indicator */}
        <div className='rounded-full bg-white/20 px-2 py-0.5 text-xs font-medium text-white'>
          <svg className='inline h-3 w-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z' />
          </svg>
        </div>
      </div>

      {/* Body */}
      <div className='px-4 py-3'>
        {/* Node Type Badge */}
        <div className='mb-2 flex items-center gap-2'>
          <span className='text-xs text-gray-500 dark:text-dark-text-secondary'>Step:</span>
          <span
            className={`rounded-full bg-${config.color}-100 px-2 py-0.5 text-xs font-medium text-${config.color}-700 dark:bg-${config.color}-900/30 dark:text-${config.color}-400`}
          >
            {config.label}
          </span>
        </div>

        {/* System Message Preview */}
        {hasPrompt ? (
          <div className='mb-2'>
            <div className='truncate text-xs text-gray-500 dark:text-dark-text-secondary'>&quot;{data.systemMessage!.substring(0, 50)}...&quot;</div>
          </div>
        ) : (
          <div className='mb-2 rounded-lg bg-yellow-50 p-2 text-xs text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300'>
            Click to configure prompt
          </div>
        )}

        {/* Prompt editable indicator */}
        <div className='mt-2 flex items-center gap-1 text-xs text-gray-400 dark:text-dark-text-secondary'>
          <svg className='h-3 w-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z' />
          </svg>
          <span>Click to edit prompt</span>
        </div>
      </div>

      {/* Output Handle (bottom) */}
      <Handle type='source' position={Position.Bottom} className={`!h-3 !w-3 !border-2 !border-white bg-gradient-to-r ${config.bgGradient}`} />
    </div>
  );
});

OrchestratorNode.displayName = 'OrchestratorNode';
